import { useCallback, useRef } from "react"

import {
  mapCommandFailure,
  type CommandFailureFields,
  type RemoteCommand,
  type RemoteCommandEventDefinitions,
  type RemoteCommandResult,
} from "../remote/remote-command"
import { useApplicationAction } from "./application-action"
import { useApplicationScope } from "./application-provider"
import {
  editSessionRejected,
  editSessionSaved,
  type EditSessionFieldName,
  type EditSessionSaveContext,
  type EditSessionSaveResult,
} from "./edit-session"

export interface EditSessionCommandOptions<
  TValues extends object,
  TValue,
  TEvents extends RemoteCommandEventDefinitions | undefined,
> {
  /** Which declared failure event of the command belongs to which field. Other messages become form errors. */
  readonly fields?: CommandFailureFields<TEvents, EditSessionFieldName<TValues>>
  /**
   * Reads the canonical values and new revision from the command's value. Without it, the submitted draft
   * becomes the new baseline and the baseline revision is kept.
   */
  readonly saved?: (value: TValue | null) => { readonly value?: TValues; readonly revision?: unknown } | undefined
  /** Optional owner lifetime, for example a use-case signal. */
  readonly signal?: AbortSignal
}

export type EditSessionCommandSave<TValues extends object> = (
  value: Readonly<TValues>,
  context: EditSessionSaveContext
) => Promise<EditSessionSaveResult<TValues>>

/**
 * Creates the `save` of an edit session that runs one business command in an application action.
 * A failed command becomes field and form errors, a technical error becomes the session's `saveError`.
 * `build` runs inside the action, so an exception it throws is a technical error too.
 */
export function useEditSessionCommand<
  TValues extends object,
  TValue,
  TEvents extends RemoteCommandEventDefinitions | undefined,
>(
  build: (
    value: Readonly<TValues>,
    context: EditSessionSaveContext
  ) => RemoteCommand<unknown, RemoteCommandResult<TValue>, TEvents>,
  options: EditSessionCommandOptions<TValues, TValue, TEvents> = {}
): EditSessionCommandSave<TValues> {
  const scope = useApplicationScope()
  const action = useApplicationAction({ concurrency: "reject", signal: options.signal })
  const latestRef = useRef({ build, options, scope })
  latestRef.current = { build, options, scope }
  const run = action.run

  return useCallback(
    async (value, context) => {
      let command: RemoteCommand<unknown, RemoteCommandResult<TValue>, TEvents> | undefined
      const result = await run(({ command: execute }) => {
        command = latestRef.current.build(value, context)
        return execute(command)
      })
      const { options, scope } = latestRef.current

      switch (result.status) {
        case "succeeded": {
          const saved = options.saved?.(result.value.value)
          return editSessionSaved(saved?.value, saved?.revision)
        }
        case "failed": {
          // The action ran only this command, so it is set once a failure is reported.
          const messages = mapCommandFailure(result.failure, command!, options.fields)
          const hasFieldMessages = Object.keys(messages.fields).length > 0
          const form =
            messages.form.length > 0 || hasFieldMessages
              ? messages.form
              : [scope.localization.i18n.t(($) => $.errors.saveFailed, { ns: "florisoft-framework" })]
          return editSessionRejected<TValues>({
            form: form.length > 0 ? form : undefined,
            fields: messages.fields as Partial<Record<EditSessionFieldName<TValues>, readonly string[]>>,
          })
        }
        case "error":
          throw result.error
        case "aborted":
          throw new DOMException("The save was aborted.", "AbortError")
        case "rejected":
          throw new Error("A save of this edit session is already running.")
      }
    },
    [run]
  )
}
